import { useFlags } from 'launchdarkly-react-client-sdk';
import { parseAsArrayOf, parseAsString, parseAsStringLiteral, useQueryState, useQueryStates } from 'nuqs';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { CHANNEL_REQUIREMENTS, CHANNEL_TO_PARTNER, PARTNER_TO_CHANNEL } from '~/constants';
import { LDFeatureFlag } from '~/types/feature-flag';
import { MarketplaceChannel, MarketplaceLead, marketplaceSort } from '~/types/marketplace';
import { parseAsMarketplaceChannel } from '~/utils/channels';

type UserVendorsListParamsParams = {
  lead: MarketplaceLead | null;
};

export const useVendorsListUrlParams = ({ lead }: UserVendorsListParamsParams) => {
  const { marketplaceSorting } = useFlags<LDFeatureFlag>();
  const navigate = useNavigate();

  const [leadIdParam] = useQueryState('leadId');

  const [channel] = useQueryState('channel', parseAsMarketplaceChannel.withDefault('brighte'));
  const partner = CHANNEL_TO_PARTNER(channel);

  const [urlPostcode] = useQueryState('postcode');
  const fixedPostcode = CHANNEL_REQUIREMENTS[channel as MarketplaceChannel]?.fixedPostcode;
  const defaultPostcode = fixedPostcode ?? lead?.postcode ?? '';

  const [queryParams, setQueryParams] = useQueryStates({
    postcode: parseAsString.withDefault(defaultPostcode),
    suburb: parseAsString.withDefault(lead?.city ?? ''),
    manufacturerBrands: parseAsArrayOf(parseAsString).withDefault([]),
    categories: parseAsArrayOf(parseAsString).withDefault(lead?.categorySlugs ?? []),
    promotions: parseAsArrayOf(parseAsString).withDefault([]),
    sort: parseAsStringLiteral(marketplaceSort).withDefault(marketplaceSorting ?? 'RANDOM'),
    synthetic: parseAsString,
  });
  const { sort } = queryParams;

  useEffect(() => {
    let shouldNavigate = false;
    const params = new URLSearchParams(window.location.search);

    if (lead?.partner) {
      const channelFromLead = lead.partner ? (PARTNER_TO_CHANNEL(lead.partner) ?? null) : null;
      if (channelFromLead && channelFromLead !== channel) {
        params.set('channel', channelFromLead);
        shouldNavigate = true;
      }
    }

    // Set initial postcode if not in URL but available from lead or fixed postcode
    if (!urlPostcode && defaultPostcode) {
      setQueryParams(
        (current) => ({
          ...current,
          postcode: defaultPostcode,
          ...(lead?.city && !current.suburb && { suburb: lead.city }),
        }),
        { history: 'replace' },
      );
    }

    if (shouldNavigate) {
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [sort, lead?.partner, channel, navigate, urlPostcode, defaultPostcode, setQueryParams, lead?.city]);

  return {
    leadIdParam,
    channel,
    partner,
    ...queryParams,
    setQueryParams,
  };
};
